import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  readTabFromQueryParams,
  syncTabToUrl,
  TOURFOLD_SALES_EMAIL,
} from '@tourfold/libraries/shared-util';
import { provideBillingAiPageContext } from './settings-billing.ai-page-context';
import {
  ColumnDef,
  SettingsLoadingPlaceholder,
  TfColumnDefDirective,
  TfDataTableComponent,
} from '@tourfold/libraries/shared-ui';
import type {
  BillingInvoiceAccess,
  BillingOverview,
  BillingPlan,
  BillingPlanChange,
  BillingPlanMeterEntitlementDto,
  BillingPlanPriceItem,
  UsageMeterPeriodValue,
  UsageSpendLimitsResponseDto,
  UsageSpendLimitUpdateDtoEnforcementModeEnum,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import {
  BillingApi,
  UsageApi,
  UsageSpendLimitsControllerApi,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { BillingInvoicesApi } from '@tourfold/libraries/tf-api-client-public-v2';
import type {
  BillingInvoiceList as PublicBillingInvoiceList,
  BillingInvoiceSummary as PublicBillingInvoiceSummary,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { BillingMeter } from './billing-meter/billing-meter';
import type { BillingUsageMeter } from './billing-meter/billing-meter';
import type {
  MeterPricingDisplay,
  MeterPricingTierRow,
  MeterPricingTiers,
} from './billing-meter/billing-meter.types';
import { BillingPricingTiersDialog } from './billing-pricing-tiers-dialog/billing-pricing-tiers-dialog';
import { UpdateSeatsDialog } from './update-seats-dialog/update-seats-dialog';
import { PlanChangeDialog } from './plan-change-dialog/plan-change-dialog';
import { BillingProfileTab } from './billing-profile-tab/billing-profile-tab';
import { PlatformI18nService } from '@tourfold/shared-kernels/i18n';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

const TAB_VALUES = ['plan', 'profile', 'invoices', 'cancellation'] as const;
type BillingTab = (typeof TAB_VALUES)[number];
type PlanKind = BillingPlan['planKind'];
type BillingInvoiceAccessMode = BillingInvoiceAccess['mode'];
type BillingPlanChangeMode = BillingPlanChange['mode'];

const PLAN_KIND = {
  Free: 'FREE',
  SelfServe: 'SELF_SERVE',
  Enterprise: 'ENTERPRISE',
} as const satisfies Record<string, PlanKind>;

const INVOICE_ACCESS_MODE = {
  Download: 'DOWNLOAD',
  NoInvoicesFree: 'NO_INVOICES_FREE',
  EnterpriseManaged: 'ENTERPRISE_MANAGED',
  NoActiveSubscription: 'NO_ACTIVE_SUBSCRIPTION',
} as const satisfies Record<string, BillingInvoiceAccessMode>;

const PLAN_CHANGE_MODE = {
  SelfService: 'SELF_SERVICE',
  EnterpriseLocked: 'ENTERPRISE_LOCKED',
  NoActiveSubscription: 'NO_ACTIVE_SUBSCRIPTION',
} as const satisfies Record<string, BillingPlanChangeMode>;

interface BillingInvoiceTableRow extends PublicBillingInvoiceSummary {
  id: string;
  amountDisplay: string;
}

const BILLING_PROBLEM = {
  PlanChangeInProgress: 'https://problems.tourfold.com/billing/plan-change-in-progress',
  PlanChangeNotAllowed: 'https://problems.tourfold.com/billing/plan-change-not-allowed',
  StripeBillingUnavailable: 'https://problems.tourfold.com/billing/stripe-billing-unavailable',
  TenantBillingLocked: 'https://problems.tourfold.com/tenant-billing-locked',
} as const;

type PlanCompareCellKind = 'text' | 'check';

interface PlanCompareCell {
  kind: PlanCompareCellKind;
  /** Translation key (with namespace) or a literal already-formatted string. */
  value: string;
  /** Set when `value` is a translation key that should be passed through `| translate`. */
  isTranslationKey?: boolean;
}

interface PlanCompareRow {
  id: string;
  labelKey: string;
  cells: PlanCompareCell[];
}

type SpendCapPreset =
  | 'unlimited'
  | 'amount-0'
  | 'amount-50'
  | 'amount-100'
  | 'amount-500'
  | 'amount-1000'
  | 'custom';

interface SpendCapOption {
  value: SpendCapPreset;
  labelKey: 'unlimited' | 'block' | 'custom' | 'amount';
  amount?: number;
}

const SPEND_CAP_OPTIONS: ReadonlyArray<SpendCapOption> = [
  { value: 'unlimited', labelKey: 'unlimited' },
  { value: 'amount-0', labelKey: 'block', amount: 0 },
  { value: 'amount-50', labelKey: 'amount', amount: 50 },
  { value: 'amount-100', labelKey: 'amount', amount: 100 },
  { value: 'amount-500', labelKey: 'amount', amount: 500 },
  { value: 'amount-1000', labelKey: 'amount', amount: 1000 },
  { value: 'custom', labelKey: 'custom' },
];

const STATIC_CELL_CHECK: PlanCompareCell = { kind: 'check', value: '' };
const STATIC_CELL_DASH: PlanCompareCell = { kind: 'text', value: '—' };
const STORAGE_METER_KEY = 'storage-size';
const CUSTOM_OBJECTS_METER_KEY = 'custom-objects-count';
const AI_INPUT_TOKENS_METER_KEY = 'ai-input-tokens';
const AI_OUTPUT_TOKENS_METER_KEY = 'ai-output-tokens';
const SMS_METER_KEY = 'sms-count';
const ACCOUNT_SPEND_LIMIT_KEY = 'account';
const BYTES_PER_MB = 1_000_000;
const BYTES_PER_KB = 1_000;
const BYTES_PER_GB = 1_000_000_000;
const AI_TOKENS_PER_BILLING_UNIT = 1_000;
const USAGE_METER_KEYS = [
  CUSTOM_OBJECTS_METER_KEY,
  STORAGE_METER_KEY,
  SMS_METER_KEY,
  AI_INPUT_TOKENS_METER_KEY,
  AI_OUTPUT_TOKENS_METER_KEY,
] as const;
type UsageMeterKey = (typeof USAGE_METER_KEYS)[number];
type UsageMeterValueMap = Partial<Record<UsageMeterKey, UsageMeterPeriodValue>>;
type BillingUsageMeterRow = readonly [BillingUsageMeter | null, BillingUsageMeter | null];

const BILLING_USAGE_METER_CONFIGS: ReadonlyArray<{
  meterKey: UsageMeterKey;
  labelKey: string;
}> = [
  {
    meterKey: CUSTOM_OBJECTS_METER_KEY,
    labelKey: 'SETTINGS_BILLING.PLAN.METERS.OBJECTS',
  },
  {
    meterKey: STORAGE_METER_KEY,
    labelKey: 'SETTINGS_BILLING.PLAN.METERS.STORAGE',
  },
  {
    meterKey: SMS_METER_KEY,
    labelKey: 'SETTINGS_BILLING.PLAN.METERS.SMS',
  },
  {
    meterKey: AI_INPUT_TOKENS_METER_KEY,
    labelKey: 'SETTINGS_BILLING.PLAN.METERS.AI_INPUT_TOKENS',
  },
  {
    meterKey: AI_OUTPUT_TOKENS_METER_KEY,
    labelKey: 'SETTINGS_BILLING.PLAN.METERS.AI_OUTPUT_TOKENS',
  },
];

const SUPPORT_LABEL_BY_PLAN_KIND: Record<PlanKind, string> = {
  FREE: 'SETTINGS_BILLING.COMPARE.SUPPORT.COMMUNITY',
  SELF_SERVE: 'SETTINGS_BILLING.COMPARE.SUPPORT.EMAIL',
  ENTERPRISE: 'SETTINGS_BILLING.COMPARE.SUPPORT.DEDICATED',
};

const ENTERPRISE_TEASER_ID = '__enterprise_teaser__';
const ENTERPRISE_CONTACT_EMAIL = TOURFOLD_SALES_EMAIL;
const ALL_ENTITLEMENT_FEATURE_KEYS = [
  'ai-assistant',
  'document-management',
  'mcp-access',
  'webhooks',
  'audit-log',
  'custom-domains',
  'themes',
  'brands',
  'sso-login',
];
const ENTITLEMENT_FEATURE_ORDER = new Map(
  ALL_ENTITLEMENT_FEATURE_KEYS.map((featureKey, index) => [featureKey, index])
);

const ENTERPRISE_TEASER_PLAN: BillingPlan = {
  id: ENTERPRISE_TEASER_ID,
  name: 'Enterprise',
  description: undefined,
  planKind: PLAN_KIND.Enterprise,
  entitlementFeatureKeys: ALL_ENTITLEMENT_FEATURE_KEYS,
  meterEntitlements: [],
  priceItems: [],
  isActive: true,
  isPublic: false,
};

@Component({
  selector: 'tf-settings-page-billing',
  imports: [
    CommonModule,
    TranslateModule,
    TabsModule,
    ButtonModule,
    TagModule,
    TfDataTableComponent,
    TfColumnDefDirective,
    BillingMeter,
    BillingPricingTiersDialog,
    UpdateSeatsDialog,
    PlanChangeDialog,
    BillingProfileTab,
    SettingsInlineMessage,
    SettingsLoadError,
    SettingsLoadingPlaceholder,
  ],
  templateUrl: './settings-billing.component.html',
  styleUrls: [
    './settings-billing.component.scss',
    './settings-billing-current-plan.scss',
    './settings-billing-usage.scss',
    './settings-billing-plan-comparison.scss',
    './settings-billing-invoices.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingSettings {
  readonly billingSkeletonRows = [1, 2, 3, 4] as const;
  readonly invoiceAccessModes = INVOICE_ACCESS_MODE;
  readonly planKinds = PLAN_KIND;

  private readonly billingFrontendApi = inject(BillingApi);
  private readonly billingInvoicesApi = inject(BillingInvoicesApi);
  private readonly usageApi = inject(UsageApi);
  private readonly usageSpendLimitsApi = inject(UsageSpendLimitsControllerApi);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly platformI18n = inject(PlatformI18nService);

  readonly activeTab = signal<BillingTab>(this.readBillingTab(this.route.snapshot.queryParams));
  readonly overview = signal<BillingOverview | null>(null);
  readonly usageMeterValues = signal<UsageMeterValueMap>({});
  readonly failedUsageMeterKeys = signal<ReadonlySet<UsageMeterKey>>(new Set());
  readonly invoices = signal<BillingInvoiceTableRow[]>([]);
  readonly spendLimits = signal<UsageSpendLimitsResponseDto | null>(null);
  readonly isLoadingOverview = signal(true);
  readonly isLoadingInvoices = signal(false);
  readonly isLoadingUsageMeters = signal(false);
  readonly isLoadingSpendLimits = signal(false);
  readonly overviewHasLoadedOnce = signal(false);
  readonly invoicesHaveLoadedOnce = signal(false);
  readonly overviewLoadFailed = signal(false);
  readonly overviewLoadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly invoicesLoadFailed = signal(false);
  readonly invoiceLoadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly spendLimitsLoadFailed = signal(false);
  readonly isSavingSpendLimit = signal(false);
  readonly spendLimitSaveFailed = signal(false);
  readonly spendLimitSaveSucceeded = signal(false);
  readonly spendCapPreset = signal<SpendCapPreset>('amount-50');
  readonly spendCapCustomMajor = signal<number>(50);
  readonly spendCapDirty = signal(false);
  readonly invoiceDownloadFailed = signal(false);
  readonly downloadingInvoiceId = signal<string | null>(null);
  readonly switchingPlanId = signal<string | null>(null);
  readonly isCancelingPlanChange = signal(false);
  readonly isCancelingSubscription = signal(false);
  readonly isReconcilingPlanChange = signal(false);
  readonly planSwitchFailed = signal<string | null>(null);
  readonly planSwitchErrorKey = signal<string | null>(null);
  readonly planSwitchErrorMessageKey = computed(
    () => this.planSwitchErrorKey() ?? 'SETTINGS_BILLING.PLAN_CARDS.SWITCH_FAILED'
  );
  readonly planSwitchStarted = signal<string | null>(null);
  readonly planChangeDialogTarget = signal<BillingPlan | null>(null);
  readonly isChangingSeats = signal(false);
  readonly seatChangeFailed = signal(false);
  readonly seatChangeStarted = signal(false);
  readonly seatChangeErrorKey = signal<string | null>(null);
  readonly seatChangeErrorMessageKey = computed(
    () => this.seatChangeErrorKey() ?? 'SETTINGS_BILLING.PLAN.SEATS.CHANGE_FAILED'
  );
  readonly planCancelFailed = signal(false);
  readonly planCancelErrorKey = signal<string | null>(null);
  readonly planCancelErrorMessageKey = computed(
    () => this.planCancelErrorKey() ?? 'SETTINGS_BILLING.PENDING_CHANGE.CANCEL_FAILED'
  );
  readonly subscriptionCancelFailed = signal(false);
  readonly subscriptionCancelStarted = signal(false);
  readonly isSeatDialogOpen = signal(false);
  readonly pricingDialog = signal<MeterPricingTiers | null>(null);

  readonly showInitialOverviewLoading = computed(
    () => this.isLoadingOverview() && !this.overviewHasLoadedOnce() && !this.overviewLoadFailed()
  );
  readonly showInitialOverviewError = computed(
    () => this.overviewLoadFailed() && !this.overviewHasLoadedOnce()
  );
  readonly showOverviewReloadError = computed(
    () => this.overviewLoadFailed() && this.overviewHasLoadedOnce()
  );
  readonly showInitialInvoicesLoading = computed(
    () => this.isLoadingInvoices() && !this.invoicesHaveLoadedOnce() && !this.invoicesLoadFailed()
  );
  readonly showInitialInvoicesError = computed(
    () => this.invoicesLoadFailed() && !this.invoicesHaveLoadedOnce()
  );
  readonly showInvoiceReloadError = computed(
    () => this.invoicesLoadFailed() && this.invoicesHaveLoadedOnce()
  );

  readonly currentSubscription = computed(() => this.overview()?.currentSubscription ?? null);
  readonly currentPlan = computed(() => this.currentSubscription()?.plan ?? null);
  readonly billingState = computed(() => this.overview()?.billingState ?? null);
  readonly seatUsage = computed(() => this.overview()?.seatUsage ?? null);
  readonly billingUsageMeters = computed<BillingUsageMeter[]>(() => {
    const currentPlan = this.currentPlan();
    if (!currentPlan) {
      return [];
    }

    const meters: BillingUsageMeter[] = [];

    for (const config of BILLING_USAGE_METER_CONFIGS) {
      meters.push(this.toBillingUsageMeter(currentPlan, config));
    }

    return meters;
  });
  private readonly billingUsageMetersById = computed(() => {
    const byId = new Map<UsageMeterKey, BillingUsageMeter>();
    for (const meter of this.billingUsageMeters()) {
      byId.set(meter.id as UsageMeterKey, meter);
    }
    return byId;
  });
  readonly billingResourceMeterRows = computed<ReadonlyArray<BillingUsageMeterRow>>(() => {
    const byId = this.billingUsageMetersById();
    const rows: BillingUsageMeterRow[] = [
      [
        byId.get(CUSTOM_OBJECTS_METER_KEY) ?? null,
        byId.get(STORAGE_METER_KEY) ?? null,
      ],
      [byId.get(SMS_METER_KEY) ?? null, null],
    ];
    return rows.filter(([left, right]) => left != null || right != null);
  });
  readonly billingAiUsageMeters = computed<BillingUsageMeter[]>(() => {
    const byId = this.billingUsageMetersById();
    return [byId.get(AI_INPUT_TOKENS_METER_KEY), byId.get(AI_OUTPUT_TOKENS_METER_KEY)].filter(
      (meter): meter is BillingUsageMeter => meter != null
    );
  });
  readonly hasUsageMeterLoadFailures = computed(() => this.failedUsageMeterKeys().size > 0);
  readonly billingStateStatus = computed(() => this.billingState()?.status ?? null);
  readonly isBillingCanceling = computed(() => this.billingStateStatus() === 'CANCELING');
  readonly isBillingStatusWarning = computed(() => {
    const status = this.billingStateStatus();
    return status != null && status !== 'ACTIVE';
  });
  readonly isEnterprisePlan = computed(() => this.currentPlan()?.planKind === PLAN_KIND.Enterprise);
  readonly canShowCancellationTab = computed(() => {
    const kind = this.currentPlan()?.planKind;
    return kind === PLAN_KIND.SelfServe || kind === PLAN_KIND.Free;
  });
  // Card-on-file management only makes sense on paid self-service: Free tenants
  // have no Stripe customer yet; Enterprise is billed via a custom contract.
  readonly canShowPaymentMethodCard = computed(
    () => this.currentPlan()?.planKind === PLAN_KIND.SelfServe
  );
  readonly cancellationEffectiveAt = computed(
    () =>
      this.currentSubscription()?.providerCancelAt ??
      this.currentSubscription()?.providerCurrentPeriodEnd ??
      null
  );
  readonly canCancelSubscription = computed(
    () =>
      this.canShowCancellationTab() &&
      !this.isBillingCanceling() &&
      !this.hasPendingPlanChange() &&
      !this.isCancelingSubscription()
  );
  readonly planChangeMode = computed(() => this.overview()?.planChange?.mode ?? null);
  readonly planChangeDialogCurrentPrice = computed(() => {
    const plan = this.currentPlan();
    return plan ? this.formatPlanPrimaryPrice(plan) : null;
  });
  readonly planChangeDialogCurrentPriceSuffix = computed(() => {
    const plan = this.currentPlan();
    return plan ? this.planPriceSuffix(plan) : null;
  });
  readonly planChangeDialogTargetPrice = computed(() => {
    const plan = this.planChangeDialogTarget();
    return plan ? this.formatPlanPrimaryPrice(plan) : null;
  });
  readonly planChangeDialogTargetPriceSuffix = computed(() => {
    const plan = this.planChangeDialogTarget();
    return plan ? this.planPriceSuffix(plan) : null;
  });
  readonly planChangeDialogRequiresBillingTerms = computed(() => {
    const plan = this.planChangeDialogTarget();
    return plan ? this.requiresPlanConsent(plan) : false;
  });
  readonly pendingChange = computed(() => this.overview()?.pendingChange ?? null);
  readonly pendingChangePlanName = computed(() => this.pendingChange()?.targetPlan?.name ?? null);
  readonly pendingChangeSeatQuantity = computed(
    () => this.pendingChange()?.targetSeatQuantity ?? null
  );
  readonly hasPendingPlanChange = computed(() => this.pendingChange() != null);
  readonly canChangeSeats = computed(() => this.seatUsage()?.canChange === true);
  readonly seatsUnlimited = computed(() => {
    const usage = this.seatUsage();
    return usage?.source === 'UNLIMITED' || usage?.limit == null || usage.limit === 0;
  });
  readonly seatsRemainingCount = computed(() => {
    const usage = this.seatUsage();
    const used = usage?.used ?? 0;
    if (!usage?.limit) return 0;
    return Math.max(0, usage.limit - used);
  });
  readonly seatsUsedPercent = computed(() => {
    const usage = this.seatUsage();
    const used = usage?.used ?? 0;
    if (!usage?.limit || usage.limit <= 0) return 0;
    return Math.min(100, (used / usage.limit) * 100);
  });
  readonly seatsActiveUsers = computed(() => Math.max(1, this.seatUsage()?.used ?? 1));
  readonly seatsCurrent = computed(
    () => this.pendingChangeSeatQuantity() ?? this.seatUsage()?.limit ?? this.seatsActiveUsers()
  );
  readonly seatsPricePerSeatMinor = computed<number | null>(() => {
    const plan = this.currentPlan();
    if (!plan) return null;
    const seatItem = this.primarySeatPriceItem(plan);
    return seatItem?.unitAmount ?? null;
  });
  readonly seatsCurrency = computed<string>(() => {
    const plan = this.currentPlan();
    if (!plan) return 'eur';
    const seatItem = this.primarySeatPriceItem(plan);
    return seatItem?.currency ?? 'eur';
  });
  readonly billingPeriodLabel = computed(() => {
    const subscription = this.currentSubscription();
    return this.formatBillingPeriod(
      subscription?.providerCurrentPeriodStart ?? null,
      subscription?.providerCurrentPeriodEnd ?? null
    );
  });
  readonly nextInvoiceLabel = computed(() => {
    const periodEnd = this.currentSubscription()?.providerCurrentPeriodEnd;
    return periodEnd ? this.formatDate(periodEnd) : null;
  });
  /**
   * Free / Enterprise plans have no Stripe billing period, but usage meters still tick on
   * a UTC calendar-month boundary. Surface that window as "Usage period" so admins can see
   * when meters reset. Picks any available meter value (all share the same period).
   */
  readonly usagePeriodLabel = computed(() => {
    const values = this.usageMeterValues();
    const sample = Object.values(values).find((v) => v?.periodStartedAt && v?.periodEndsAt);
    if (!sample) return null;
    return this.formatBillingPeriod(sample.periodStartedAt, sample.periodEndsAt);
  });
  readonly planOptions = computed(() => {
    const currentPlan = this.currentPlan();
    if (currentPlan?.planKind === PLAN_KIND.Enterprise) {
      return [];
    }

    const plansById = new Map<string, BillingPlan>();
    if (currentPlan) {
      plansById.set(currentPlan.id, currentPlan);
    }
    for (const plan of this.overview()?.availablePlans ?? []) {
      plansById.set(plan.id, plan);
    }

    const includeFreePlans = currentPlan?.planKind === PLAN_KIND.Free;

    return Array.from(plansById.values())
      .filter(
        (plan) =>
          plan.planKind !== PLAN_KIND.Free || includeFreePlans || plan.id === currentPlan?.id
      )
      .sort((first, second) => {
        const amountDelta = this.planSeatSortAmount(first) - this.planSeatSortAmount(second);
        if (amountDelta !== 0) return amountDelta;
        return first.name.localeCompare(second.name);
      });
  });
  readonly comparePlans = computed<BillingPlan[]>(() => {
    const plans = this.planOptions();
    if (plans.length === 0) {
      return [];
    }
    const hasEnterpriseColumn = plans.some((plan) => plan.planKind === PLAN_KIND.Enterprise);
    return hasEnterpriseColumn ? plans : [...plans, ENTERPRISE_TEASER_PLAN];
  });

  readonly enterpriseContactEmail = ENTERPRISE_CONTACT_EMAIL;

  readonly compareRows = computed<PlanCompareRow[]>(() => {
    const plans = this.comparePlans();
    if (plans.length === 0) {
      return [];
    }

    const leadingStaticRows: PlanCompareRow[] = [
      {
        id: 'objects-included',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.OBJECTS_INCLUDED',
        cells: plans.map((plan) => this.objectsIncludedCell(plan)),
      },
      {
        id: 'extra-object-pricing',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.EXTRA_OBJECT_PRICING',
        cells: plans.map((plan) => this.extraObjectPricingCell(plan)),
      },
      {
        id: 'sms-included',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.SMS_INCLUDED',
        cells: plans.map((plan) => this.meterIncludedCell(plan, SMS_METER_KEY)),
      },
      {
        id: 'sms-pricing',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.SMS_PRICING',
        cells: plans.map((plan) => this.extraMeterPricingCell(plan, SMS_METER_KEY)),
      },
      {
        id: 'storage-included',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.STORAGE_INCLUDED',
        cells: plans.map((plan) => this.storageIncludedCell(plan)),
      },
      {
        id: 'extra-storage-pricing',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.EXTRA_STORAGE_PRICING',
        cells: plans.map((plan) => this.extraStoragePricingCell(plan)),
      },
      {
        id: 'ai-input-included',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.AI_INPUT_INCLUDED',
        cells: plans.map((plan) => this.meterIncludedCell(plan, AI_INPUT_TOKENS_METER_KEY)),
      },
      {
        id: 'ai-input-pricing',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.AI_INPUT_PRICING',
        cells: plans.map((plan) => this.extraMeterPricingCell(plan, AI_INPUT_TOKENS_METER_KEY)),
      },
      {
        id: 'ai-output-included',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.AI_OUTPUT_INCLUDED',
        cells: plans.map((plan) => this.meterIncludedCell(plan, AI_OUTPUT_TOKENS_METER_KEY)),
      },
      {
        id: 'ai-output-pricing',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.AI_OUTPUT_PRICING',
        cells: plans.map((plan) => this.extraMeterPricingCell(plan, AI_OUTPUT_TOKENS_METER_KEY)),
      },
      {
        id: 'tour-planning',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.TOUR_PLANNING',
        cells: plans.map(() => STATIC_CELL_CHECK),
      },
      {
        id: 'mobile-app',
        labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.MOBILE_APP',
        cells: plans.map(() => STATIC_CELL_CHECK),
      },
    ];

    const featureRows = this.entitlementFeatureRows(plans);

    const supportRow: PlanCompareRow = {
      id: 'support',
      labelKey: 'SETTINGS_BILLING.COMPARE.ROWS.SUPPORT',
      cells: plans.map((plan) => ({
        kind: 'text' as const,
        value: SUPPORT_LABEL_BY_PLAN_KIND[plan.planKind] ?? '—',
        isTranslationKey: true,
      })),
    };

    return [...leadingStaticRows, ...featureRows, supportRow];
  });
  readonly invoiceAccessMode = computed(() => this.overview()?.invoiceAccess.mode ?? null);
  readonly canDownloadInvoices = computed(
    () => this.invoiceAccessMode() === INVOICE_ACCESS_MODE.Download
  );
  readonly invoiceTableRows = computed<BillingInvoiceTableRow[]>(() =>
    this.invoices().map((invoice) => ({
      ...invoice,
      amountDisplay: this.formatAmount(invoice.amountPaid ?? invoice.amountDue, invoice.currency),
    }))
  );
  readonly accountSpendLimit = computed(
    () =>
      this.spendLimits()?.limits.find((limit) => limit.meterKey === ACCOUNT_SPEND_LIMIT_KEY) ?? null
  );
  readonly accountThresholdSyncState = computed(
    () =>
      this.spendLimits()?.thresholdSyncStates.find(
        (state) => state.thresholdKey === ACCOUNT_SPEND_LIMIT_KEY
      ) ?? null
  );
  readonly spendCapEffectiveMinor = computed<number | null>(() => {
    const preset = this.spendCapPreset();
    if (preset === 'unlimited') return null;
    if (preset === 'custom') {
      const major = this.spendCapCustomMajor();
      return Number.isFinite(major) ? Math.max(0, Math.round(major * 100)) : null;
    }
    const option = SPEND_CAP_OPTIONS.find((opt) => opt.value === preset);
    return option?.amount != null ? option.amount * 100 : null;
  });
  readonly spendCapDisabledDraft = computed(() => this.spendCapPreset() === 'unlimited');
  readonly spendCapSummaryKey = computed<string>(() => {
    if (this.spendCapPreset() === 'unlimited') {
      return 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SUMMARY_UNCAPPED';
    }
    const minor = this.spendCapEffectiveMinor();
    if (minor === 0) {
      return 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SUMMARY_BLOCKING';
    }
    return 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SUMMARY_CAPPED';
  });
  readonly spendCapOptions = SPEND_CAP_OPTIONS;
  readonly canSaveAccountSpendLimit = computed(() => {
    if (this.isSavingSpendLimit()) {
      return false;
    }
    const limit = this.accountSpendLimit();
    const effectiveMinor = this.spendCapEffectiveMinor();
    const disabled = this.spendCapDisabledDraft();
    if (!disabled && effectiveMinor == null) {
      return false;
    }
    return (
      limit != null &&
      this.spendCapDirty() &&
      (effectiveMinor !== (limit.amountMinor ?? null) || disabled !== limit.disabled)
    );
  });

  readonly invoiceColumns: ColumnDef[] = [
    { field: 'number', title: 'SETTINGS_BILLING.INVOICES.NUMBER', type: 'text', width: '28%' },
    {
      field: 'createdAt',
      title: 'SETTINGS_BILLING.INVOICES.DATE',
      type: 'date',
      dateFormat: 'mediumDate',
      width: '18%',
    },
    { field: 'status', title: 'SETTINGS_BILLING.INVOICES.STATUS', type: 'text', width: '16%' },
    {
      field: 'amountDisplay',
      title: 'SETTINGS_BILLING.INVOICES.AMOUNT',
      type: 'text',
      width: '18%',
    },
    { field: '_actions', title: '', type: 'custom', width: '10rem' },
  ];

  private invoicesLoaded = false;
  private reconciledCheckoutSessionId: string | null = null;
  private overviewLoadRequestId = 0;
  private invoiceLoadRequestId = 0;

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      const tab = this.readBillingTab(params);
      this.activeTab.set(tab);
      if (tab === 'invoices') {
        this.loadInvoicesIfAvailable();
      }
      const checkoutSessionId = String(params['checkout_session_id'] ?? '');
      if (
        params['billing_checkout'] === 'success' &&
        checkoutSessionId &&
        this.reconciledCheckoutSessionId !== checkoutSessionId
      ) {
        this.reconcilePendingPlanChange(checkoutSessionId);
      }
    });

    this.loadOverview();

    provideBillingAiPageContext({
      activeTab: this.activeTab,
      invoices: this.invoices,
      usageMeters: this.billingUsageMeters,
    });
  }

  onTabChange(tab: string): void {
    const nextTab =
      TAB_VALUES.includes(tab as BillingTab) && this.isTabVisible(tab as BillingTab)
        ? (tab as BillingTab)
        : 'plan';
    this.activeTab.set(nextTab);
    syncTabToUrl(this.router, this.route, 'tab', nextTab);

    if (nextTab === 'invoices') {
      this.loadInvoicesIfAvailable();
    }
  }

  refresh(): void {
    this.loadOverview();
  }

  retryInvoices(): void {
    this.invoicesLoaded = false;
    this.loadInvoicesIfAvailable();
  }

  reloadInvoicesTab(): void {
    if (!this.canDownloadInvoices() || this.isLoadingInvoices()) {
      return;
    }
    this.retryInvoices();
  }

  onSpendCapPresetChange(event: Event): void {
    const select = event.target as HTMLSelectElement | null;
    const value = (select?.value ?? 'amount-50') as SpendCapPreset;
    this.spendCapPreset.set(value);
    this.spendCapDirty.set(true);
    this.spendLimitSaveSucceeded.set(false);
    this.spendLimitSaveFailed.set(false);
  }

  onSpendCapCustomInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const value = Number(input?.value);
    this.spendCapCustomMajor.set(Number.isFinite(value) ? Math.max(0, value) : 0);
    this.spendCapDirty.set(true);
    this.spendLimitSaveSucceeded.set(false);
    this.spendLimitSaveFailed.set(false);
  }

  saveAccountSpendLimit(): void {
    const limit = this.accountSpendLimit();
    if (!limit || !this.canSaveAccountSpendLimit()) {
      return;
    }

    this.isSavingSpendLimit.set(true);
    this.spendLimitSaveFailed.set(false);
    this.spendLimitSaveSucceeded.set(false);

    from(
      this.usageSpendLimitsApi.update({
        updateUsageSpendLimitsRequestDto: {
          limits: [
            {
              meterKey: ACCOUNT_SPEND_LIMIT_KEY,
              amountMinor: this.spendCapEffectiveMinor() ?? undefined,
              currency: limit.currency ?? 'eur',
              enforcementMode:
                limit.enforcementMode as unknown as UsageSpendLimitUpdateDtoEnforcementModeEnum,
              disabled: this.spendCapDisabledDraft(),
            },
          ],
        },
      })
    )
      .pipe(
        finalize(() => this.isSavingSpendLimit.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.applySpendLimits(response);
          this.spendLimitSaveSucceeded.set(true);
        },
        error: () => {
          this.spendLimitSaveFailed.set(true);
        },
      });
  }

  openPlanChangeDialog(plan: BillingPlan): void {
    if (!this.isSwitchablePlan(plan) || this.switchingPlanId()) {
      return;
    }
    this.planSwitchFailed.set(null);
    this.planSwitchErrorKey.set(null);
    this.planSwitchStarted.set(null);
    this.planCancelFailed.set(false);
    this.planChangeDialogTarget.set(plan);
  }

  closePlanChangeDialog(): void {
    if (this.switchingPlanId()) {
      return;
    }
    this.planChangeDialogTarget.set(null);
  }

  dismissPlanSwitchError(): void {
    this.planSwitchFailed.set(null);
    this.planSwitchErrorKey.set(null);
  }

  dismissOverviewReloadError(): void {
    this.overviewLoadFailed.set(false);
    this.overviewLoadErrorDetails.set(null);
  }

  dismissInvoiceLoadError(): void {
    this.invoicesLoadFailed.set(false);
    this.invoiceLoadErrorDetails.set(null);
  }

  dismissInvoiceDownloadError(): void {
    this.invoiceDownloadFailed.set(false);
  }

  dismissSeatChangeError(): void {
    this.seatChangeFailed.set(false);
    this.seatChangeErrorKey.set(null);
  }

  dismissPlanCancelError(): void {
    this.planCancelFailed.set(false);
    this.planCancelErrorKey.set(null);
  }

  dismissSpendLimitLoadError(): void {
    this.spendLimitsLoadFailed.set(false);
  }

  dismissSpendLimitSaveError(): void {
    this.spendLimitSaveFailed.set(false);
  }

  dismissSubscriptionCancelError(): void {
    this.subscriptionCancelFailed.set(false);
  }

  onPlanChangeConfirmed(plan: BillingPlan): void {
    this.switchPlan(plan);
  }

  retryUsageMeters(): void {
    void this.loadUsageMeters();
  }

  switchPlan(plan: BillingPlan): void {
    if (!this.isSwitchablePlan(plan) || this.switchingPlanId()) {
      return;
    }

    this.switchingPlanId.set(plan.id);
    this.planSwitchFailed.set(null);
    this.planSwitchErrorKey.set(null);
    this.planSwitchStarted.set(null);
    this.planCancelFailed.set(false);

    from(
      this.billingFrontendApi.startBillingPlanChange({
        startBillingPlanChangeRequest: {
          targetPlanId: plan.id,
          acceptedBillingTerms: this.requiresPlanConsent(plan),
        },
      })
    )
      .pipe(
        finalize(() => this.switchingPlanId.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          const redirectUrl = response.actionUrl;
          if (redirectUrl) {
            this.planChangeDialogTarget.set(null);
            globalThis.location.href = redirectUrl;
            return;
          }

          this.planSwitchStarted.set(plan.name);
          this.planChangeDialogTarget.set(null);
          this.loadOverview();
        },
        error: (error: unknown) => {
          this.planSwitchFailed.set(plan.name);
          this.planSwitchErrorKey.set(this.planSwitchFailureKey(error));
          this.planChangeDialogTarget.set(null);
          if (this.problemType(error) === BILLING_PROBLEM.PlanChangeInProgress) {
            this.loadOverview();
          }
        },
      });
  }

  openSeatDialog(): void {
    if (!this.canChangeSeats()) {
      return;
    }
    this.seatChangeFailed.set(false);
    this.seatChangeStarted.set(false);
    this.seatChangeErrorKey.set(null);
    this.isSeatDialogOpen.set(true);
  }

  closeSeatDialog(): void {
    if (this.isChangingSeats()) {
      return;
    }
    this.isSeatDialogOpen.set(false);
  }

  onSeatsConfirmed(quantity: number): void {
    if (this.isChangingSeats()) {
      return;
    }

    this.isChangingSeats.set(true);
    this.seatChangeFailed.set(false);
    this.seatChangeStarted.set(false);
    this.seatChangeErrorKey.set(null);
    this.planSwitchFailed.set(null);
    this.planSwitchErrorKey.set(null);

    from(
      this.billingFrontendApi.updateBillingSeats({
        updateBillingSeatsRequest: {
          quantity,
          acceptImmediateActivation: quantity > (this.seatUsage()?.limit ?? 0),
          acceptedBillingTerms: true,
        },
      })
    )
      .pipe(
        finalize(() => this.isChangingSeats.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (response.actionUrl) {
            globalThis.location.href = response.actionUrl;
            return;
          }
          this.seatChangeStarted.set(true);
          this.isSeatDialogOpen.set(false);
          this.loadOverview();
        },
        error: (error: unknown) => {
          this.seatChangeFailed.set(true);
          this.seatChangeErrorKey.set(this.seatChangeFailureKey(error));
          if (this.problemType(error) === BILLING_PROBLEM.PlanChangeInProgress) {
            this.loadOverview();
          }
        },
      });
  }

  resumePendingPlanChange(): void {
    const actionUrl = this.pendingChange()?.actionUrl;
    if (actionUrl) {
      globalThis.location.href = actionUrl;
    }
  }

  cancelPendingPlanChange(): void {
    if (!this.canCancelPendingPlanChange() || this.isCancelingPlanChange()) {
      return;
    }

    this.isCancelingPlanChange.set(true);
    this.planCancelFailed.set(false);
    this.planCancelErrorKey.set(null);
    this.planSwitchFailed.set(null);
    this.planSwitchErrorKey.set(null);

    from(this.billingFrontendApi.cancelPendingBillingPlanChange())
      .pipe(
        finalize(() => this.isCancelingPlanChange.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.loadOverview(),
        error: (error: unknown) => {
          this.planCancelFailed.set(true);
          this.planCancelErrorKey.set(this.planCancelFailureKey(error));
          this.loadOverview();
        },
      });
  }

  cancelSubscription(): void {
    if (!this.canCancelSubscription()) {
      return;
    }

    this.isCancelingSubscription.set(true);
    this.subscriptionCancelFailed.set(false);
    this.subscriptionCancelStarted.set(false);

    from(this.billingFrontendApi.cancelBillingSubscription())
      .pipe(
        finalize(() => this.isCancelingSubscription.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (overview) => {
          this.subscriptionCancelStarted.set(true);
          this.applyOverview(overview);
        },
        error: () => {
          this.subscriptionCancelFailed.set(true);
        },
      });
  }

  canCancelPendingPlanChange(): boolean {
    const status = this.pendingChange()?.status;
    return status === 'PENDING_CHECKOUT' || status === 'SCHEDULED_PERIOD_END';
  }

  isTranslationKey(value: string): boolean {
    return value.startsWith('SETTINGS.') || value.startsWith('SETTINGS_');
  }

  billingStatusLabelKey(): string {
    switch (this.billingStateStatus()) {
      case 'CANCELING':
        return 'SETTINGS_BILLING.PLAN.STATUS_CANCELING';
      case 'PAST_DUE':
        return 'SETTINGS_BILLING.PLAN.STATUS_PAST_DUE';
      case 'CANCELED':
        return 'SETTINGS_BILLING.PLAN.STATUS_CANCELED';
      case 'LOCKED':
        return 'SETTINGS_BILLING.PLAN.STATUS_LOCKED';
      default:
        return 'SETTINGS_BILLING.PLAN.STATUS_ACTIVE';
    }
  }

  formatPrice(plan: BillingPlan): string {
    if (plan.planKind === PLAN_KIND.Free) {
      return 'SETTINGS_BILLING.PLAN_CARDS.FREE_PRICE';
    }

    const item = this.primaryBillablePriceItem(plan);
    if (!item) return 'SETTINGS_BILLING.PLAN.CUSTOM_PRICING';

    return this.priceItemSummary(item);
  }

  formatPlanPrimaryPrice(plan: BillingPlan): string {
    if (plan.planKind === PLAN_KIND.Free) {
      return 'SETTINGS_BILLING.PLAN_CARDS.FREE_PRICE';
    }

    const seatItem = this.primarySeatPriceItem(plan);
    if (seatItem?.unitAmount != null) {
      return this.formatAmount(seatItem.unitAmount, seatItem.currency);
    }

    const item = this.primaryBillablePriceItem(plan);
    if (item?.unitAmount != null) {
      return this.formatAmount(item.unitAmount, item.currency);
    }

    return 'SETTINGS_BILLING.PLAN.CUSTOM_PRICING';
  }

  planPriceSuffix(plan: BillingPlan): string | null {
    if (plan.planKind === PLAN_KIND.Free) {
      return 'SETTINGS_BILLING.PLAN_CARDS.PER_MONTH';
    }

    const seatItem = this.primarySeatPriceItem(plan);
    if (seatItem) {
      return 'SETTINGS_BILLING.PLAN_CARDS.PER_SEAT_MONTH';
    }

    const interval = this.primaryInterval(plan);
    if (!interval) {
      return null;
    }

    const normalizedInterval = interval.toUpperCase();
    if (['DAY', 'WEEK', 'MONTH', 'YEAR'].includes(normalizedInterval)) {
      return `SETTINGS_BILLING.PLAN_CARDS.INTERVAL.${normalizedInterval}`;
    }

    return `/ ${interval}`;
  }

  planPriceItems(plan: BillingPlan): BillingPlanPriceItem[] {
    return [...(plan.priceItems ?? [])].sort((first, second) => first.position - second.position);
  }

  priceItemSummary(item: BillingPlanPriceItem, fallbackCurrency?: string | null): string {
    const meteredObjectSummary = this.meteredObjectPriceSummary(item, fallbackCurrency);
    if (meteredObjectSummary) {
      return meteredObjectSummary;
    }

    const parts: string[] = [];
    const label = item.unitLabel ?? item.productName ?? 'unit';

    if (item.unitAmount != null) {
      parts.push(this.formatAmount(item.unitAmount, item.currency ?? fallbackCurrency));
    } else if (item.billingScheme === 'tiered' || item.tiersMode) {
      parts.push(this.translate.instant('SETTINGS_BILLING.PLAN_CARDS.TIERED_PRICE'));
    } else if (item.usageType === 'metered') {
      parts.push(this.translate.instant('SETTINGS_BILLING.PLAN_CARDS.USAGE_BASED_PRICE'));
    }

    parts.push(`/ ${label}`);

    const interval = item.interval;
    if (interval) {
      parts.push(`/ ${interval}`);
    }

    if (item.usageType === 'metered') {
      parts.push(this.translate.instant('SETTINGS_BILLING.PLAN_CARDS.METERED_BADGE'));
    }

    return parts.join(' ');
  }

  formatFeatureKey(featureKey: string): string {
    return featureKey
      .split(/[-_]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  isCurrentPlan(plan: BillingPlan): boolean {
    return this.currentPlan()?.id === plan.id;
  }

  isEnterpriseTeaser(plan: BillingPlan): boolean {
    return plan.id === ENTERPRISE_TEASER_ID;
  }

  isContactSalesPlan(plan: BillingPlan): boolean {
    return plan.planKind === PLAN_KIND.Enterprise;
  }

  openContactSales(): void {
    globalThis.location.href = `mailto:${ENTERPRISE_CONTACT_EMAIL}`;
  }

  isSwitchablePlan(plan: BillingPlan): boolean {
    return (
      this.planChangeMode() === PLAN_CHANGE_MODE.SelfService &&
      plan.planKind === PLAN_KIND.SelfServe &&
      !this.isCurrentPlan(plan) &&
      !this.hasPendingPlanChange()
    );
  }

  requiresPlanConsent(plan: BillingPlan): boolean {
    return (
      this.currentPlan()?.planKind === PLAN_KIND.SelfServe && plan.planKind === PLAN_KIND.SelfServe
    );
  }

  formatAmount(amount: number | null | undefined, currency: string | null | undefined): string {
    if (amount == null) {
      return '-';
    }

    const normalizedCurrency = (currency ?? 'EUR').toUpperCase();

    try {
      return new Intl.NumberFormat(this.platformI18n.resolveFormatLocale(), {
        style: 'currency',
        currency: normalizedCurrency,
        minimumFractionDigits: amount % 1 === 0 ? 2 : 3,
        maximumFractionDigits: amount % 1 === 0 ? 2 : 4,
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(amount % 1 === 0 ? 2 : 4)} ${normalizedCurrency}`;
    }
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '-';
    }
    return new Intl.DateTimeFormat(this.platformI18n.resolveFormatLocale(), {
      dateStyle: 'medium',
    }).format(new Date(date));
  }

  private formatBillingPeriod(
    start: Date | string | null | undefined,
    exclusiveEnd: Date | string | null | undefined
  ): string | null {
    if (!start || !exclusiveEnd) {
      return null;
    }
    return this.translate.instant('SETTINGS_BILLING.PLAN.BILLING_PERIOD_VALUE', {
      start: this.formatDate(start),
      end: this.formatDate(this.displayPeriodEnd(exclusiveEnd)),
    });
  }

  private displayPeriodEnd(exclusiveEnd: Date | string): Date {
    const date = new Date(exclusiveEnd);
    date.setDate(date.getDate() - 1);
    return date;
  }

  private toBillingUsageMeter(
    plan: BillingPlan,
    config: (typeof BILLING_USAGE_METER_CONFIGS)[number]
  ): BillingUsageMeter {
    const entitlement = this.meterEntitlement(plan, config.meterKey);
    const value = this.usageMeterValues()[config.meterKey];
    const loadFailed = this.failedUsageMeterKeys().has(config.meterKey);
    const used = value?.currentQuantity ?? 0;
    const limit = this.meterLimit(entitlement);
    const included = this.meterIncludedQuantity(plan, config.meterKey, entitlement);
    const capKind = this.meterCapKind(entitlement);
    const softCap = capKind === 'soft' ? (entitlement?.limitQuantity ?? null) : null;
    const overageQuantity = Math.max(0, used - (included ?? 0));
    const overageCostMinor = this.estimatedOverageCostMinor(plan, config.meterKey, overageQuantity);
    const hardCapPercent = capKind === 'hard' && limit ? (used / limit) * 100 : 0;
    const hardCapDanger = capKind === 'hard' && hardCapPercent >= 90;
    const softCapExceeded = capKind === 'soft' && softCap != null && softCap > 0 && used > softCap;
    const tone = this.meterTone(capKind, hardCapDanger, softCapExceeded, overageQuantity, included);
    let includedPercent = this.meterIncludedPercent(used, included, limit ?? softCap);
    let overagePercent = this.meterOveragePercent(used, included, limit ?? softCap);
    const overCapPercent = this.meterOverCapPercent(used, softCap);
    // Unlimited meter with a free-tier `included` quota that the user has exceeded: the bar
    // should stay fully filled rather than shrink as `used` grows (the percent denominator
    // would otherwise track `used` itself, which is a moving target and reads as the included
    // segment shrinking the more you use). Pin the included segment at 100 % and zero out the
    // overage — the pricing footer already tells the user the per-unit rate beyond the free
    // tier, so the visual just needs to say "you're past the free quota, the meter doesn't
    // cap usage". The `info`/`accent` tone (set elsewhere via `meterTone`) keeps the fill
    // reading as informational rather than warning.
    if (capKind === 'unlimited' && included != null && included > 0 && used > included) {
      includedPercent = 100;
      overagePercent = 0;
    }
    const headerCap = included ?? limit ?? softCap;

    return {
      id: config.meterKey,
      labelKey: config.labelKey,
      used,
      limit,
      included,
      capKind,
      percent: this.meterPercent(used, limit ?? softCap ?? included),
      includedPercent,
      overagePercent,
      overCapPercent,
      valueLabel: loadFailed
        ? this.translate.instant('SETTINGS_BILLING.PLAN.METERS.LOAD_FAILED_VALUE')
        : this.formatMeterUsageHeader(config.meterKey, used, headerCap),
      loadFailed,
      pricing: this.computeMeterPricingDisplay(plan, config.meterKey),
      pricingTiers: this.buildMeterPricingTiers(plan, config),
      overageCostLabel:
        overageCostMinor != null && overageQuantity > 0
          ? this.formatAmount(
              overageCostMinor,
              this.meterPriceItem(plan, config.meterKey)?.currency
            )
          : null,
      capTone: loadFailed ? 'danger' : tone,
    };
  }

  private formatMeterUsageHeader(
    meterKey: UsageMeterKey,
    used: number,
    capNumber: number | null
  ): string {
    const formattedUsed = this.formatUsageQuantity(meterKey, used);
    if (capNumber == null || capNumber <= 0) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.VALUE_USED', {
        used: formattedUsed,
      });
    }
    return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.VALUE_OF_LIMIT', {
      used: formattedUsed,
      limit: this.formatUsageQuantity(meterKey, capNumber),
    });
  }

  private computeMeterPricingDisplay(
    plan: BillingPlan,
    meterKey: UsageMeterKey
  ): MeterPricingDisplay {
    const item = this.meterPriceItem(plan, meterKey);
    if (!item) {
      return { kind: 'none' };
    }

    const tiers = item.tiers ?? [];
    const freeTiers = tiers.filter(
      (tier) => (tier.unitAmount ?? 0) === 0 && (tier.flatAmount ?? 0) === 0 && tier.upTo != null
    );
    const paidTiers = tiers.filter(
      (tier) => (tier.unitAmount ?? 0) > 0 || (tier.flatAmount ?? 0) > 0
    );

    if (paidTiers.length === 0) {
      return { kind: 'none' };
    }

    if (freeTiers.length === 0) {
      if (paidTiers.length === 1) {
        const rate = this.formatMeterRate(plan, meterKey);
        return rate ? { kind: 'simple', rateLabel: rate } : { kind: 'none' };
      }
      return { kind: 'many' };
    }

    const freeQty = freeTiers[0]?.upTo ?? 0;
    const freeLabel = this.formatMeterFreeQuantity(meterKey, freeQty);

    if (paidTiers.length === 1) {
      const rate = this.formatMeterRate(plan, meterKey);
      return rate
        ? { kind: 'freePlusOne', freeLabel, rateLabel: rate }
        : { kind: 'freePlusMany', freeLabel };
    }

    return { kind: 'freePlusMany', freeLabel };
  }

  private buildMeterPricingTiers(
    plan: BillingPlan,
    config: (typeof BILLING_USAGE_METER_CONFIGS)[number]
  ): MeterPricingTiers | null {
    const item = this.meterPriceItem(plan, config.meterKey);
    if (!item) {
      return null;
    }

    const tiers = item.tiers ?? [];
    const paidTiers = tiers.filter(
      (tier) => (tier.unitAmount ?? 0) > 0 || (tier.flatAmount ?? 0) > 0
    );
    if (paidTiers.length < 2) {
      return null;
    }

    const unitLabel = item.unitLabel ?? this.defaultMeterBillingUnit(config.meterKey);
    const rows: MeterPricingTierRow[] = [];
    let previousUpTo = 0;
    for (const tier of tiers) {
      const rangeLabel = this.formatTierRange(config.meterKey, previousUpTo, tier.upTo ?? null);
      const priceLabel = this.formatTierPrice(tier, item.currency, unitLabel);
      rows.push({ rangeLabel, priceLabel });
      if (tier.upTo != null) {
        previousUpTo = tier.upTo;
      }
    }

    return {
      title: this.translate.instant(config.labelKey),
      rows,
    };
  }

  private formatTierRange(
    meterKey: UsageMeterKey,
    fromExclusiveBillingUnits: number,
    upToBillingUnits: number | null
  ): string {
    const fromLabel = this.formatUsageQuantity(
      meterKey,
      this.fromBillingUnits(meterKey, fromExclusiveBillingUnits)
    );
    if (upToBillingUnits == null) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.PRICING_DIALOG.RANGE_FROM', {
        from: fromLabel,
      });
    }
    return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.PRICING_DIALOG.RANGE_FROM_TO', {
      from: fromLabel,
      to: this.formatUsageQuantity(meterKey, this.fromBillingUnits(meterKey, upToBillingUnits)),
    });
  }

  private formatTierPrice(
    tier: { unitAmount?: number; flatAmount?: number },
    currency: string | null | undefined,
    unitLabel: string
  ): string {
    const unitAmount = tier.unitAmount ?? 0;
    const flatAmount = tier.flatAmount ?? 0;
    if (unitAmount === 0 && flatAmount === 0) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.PRICING_DIALOG.FREE');
    }
    if (unitAmount > 0) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.RATE', {
        price: this.formatAmount(unitAmount, currency),
        unit: unitLabel,
      });
    }
    return this.formatAmount(flatAmount, currency);
  }

  private formatMeterFreeQuantity(meterKey: UsageMeterKey, billingUnitQuantity: number): string {
    const rawQuantity = this.fromBillingUnits(meterKey, billingUnitQuantity);
    if (meterKey === STORAGE_METER_KEY) {
      return this.formatUsageQuantity(meterKey, rawQuantity);
    }
    const formattedQuantity = this.formatUsageQuantity(meterKey, rawQuantity);
    const unit = this.defaultMeterUnitLabel(meterKey);
    if (!unit) {
      return formattedQuantity;
    }
    return `${formattedQuantity} ${unit}`;
  }

  private fromBillingUnits(meterKey: UsageMeterKey, quantity: number): number {
    if (meterKey === STORAGE_METER_KEY) {
      return quantity * BYTES_PER_GB;
    }
    if (meterKey === AI_INPUT_TOKENS_METER_KEY || meterKey === AI_OUTPUT_TOKENS_METER_KEY) {
      return quantity * AI_TOKENS_PER_BILLING_UNIT;
    }
    return quantity;
  }

  private defaultMeterUnitLabel(meterKey: UsageMeterKey): string | null {
    if (meterKey === CUSTOM_OBJECTS_METER_KEY) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.UNIT_OBJECTS');
    }
    if (meterKey === AI_INPUT_TOKENS_METER_KEY || meterKey === AI_OUTPUT_TOKENS_METER_KEY) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.UNIT_TOKENS');
    }
    if (meterKey === SMS_METER_KEY) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.UNIT_SMS');
    }
    return null;
  }

  openPricingDialog(tiers: MeterPricingTiers): void {
    this.pricingDialog.set(tiers);
  }

  closePricingDialog(): void {
    this.pricingDialog.set(null);
  }

  private meterTone(
    capKind: BillingUsageMeter['capKind'],
    hardCapDanger: boolean,
    softCapExceeded: boolean,
    overageQuantity: number,
    included: number | null
  ): BillingUsageMeter['capTone'] {
    if (capKind === 'hard') {
      return hardCapDanger ? 'danger' : 'neutral';
    }
    if (capKind === 'soft') {
      return softCapExceeded ? 'danger' : 'warning';
    }
    // For unlimited meters the "over X" notion only applies when there is an actual included
    // threshold. Without one, any used > 0 would otherwise be misread as overage.
    if (included != null && included > 0 && overageQuantity > 0) {
      return 'warning';
    }
    return 'info';
  }

  private meterOverCapPercent(used: number, softCap: number | null): number {
    if (softCap == null || softCap <= 0 || used <= softCap) {
      return 0;
    }
    return Math.min(50, ((used - softCap) / softCap) * 100);
  }

  private meterEntitlement(
    plan: BillingPlan,
    meterKey: UsageMeterKey
  ): BillingPlanMeterEntitlementDto | null {
    return plan.meterEntitlements?.find((entitlement) => entitlement.meterKey === meterKey) ?? null;
  }

  private meterLimit(entitlement: BillingPlanMeterEntitlementDto | null): number | null {
    if (
      !entitlement ||
      entitlement.limitQuantity === undefined ||
      entitlement.enforcementMode !== 'hard'
    ) {
      return null;
    }
    return entitlement.limitQuantity;
  }

  private meterCapKind(
    entitlement: BillingPlanMeterEntitlementDto | null
  ): BillingUsageMeter['capKind'] {
    if (!entitlement || entitlement.limitQuantity === undefined) {
      return 'unlimited';
    }
    return entitlement.enforcementMode === 'hard' ? 'hard' : 'soft';
  }

  private meterIncludedQuantity(
    plan: BillingPlan,
    meterKey: UsageMeterKey,
    entitlement: BillingPlanMeterEntitlementDto | null
  ): number | null {
    if (entitlement?.includedQuantity != null && entitlement.includedQuantity > 0) {
      return entitlement.includedQuantity;
    }

    return this.freeTierQuantityForMeter(plan, meterKey);
  }

  private freeTierQuantityForMeter(plan: BillingPlan, meterKey: UsageMeterKey): number | null {
    const item = this.meterPriceItem(plan, meterKey);
    const freeTier = item?.tiers?.find((tier) => tier.unitAmount === 0 && tier.upTo != null);
    if (freeTier?.upTo == null) {
      return null;
    }
    return this.fromBillingUnits(meterKey, freeTier.upTo);
  }

  private meterPriceItem(plan: BillingPlan, meterKey: UsageMeterKey): BillingPlanPriceItem | null {
    return this.planPriceItems(plan).find((priceItem) => priceItem.meterKey === meterKey) ?? null;
  }

  private meterPercent(used: number, denominator: number | null | undefined): number {
    if (!denominator || denominator <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, (used / denominator) * 100));
  }

  private meterIncludedPercent(
    used: number,
    included: number | null,
    limit: number | null
  ): number {
    const denominator = limit ?? Math.max(used, included ?? 0);
    if (!included || denominator <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, (Math.min(used, included) / denominator) * 100));
  }

  private meterOveragePercent(used: number, included: number | null, limit: number | null): number {
    if (!included || used <= included) {
      return 0;
    }
    const denominator = limit ?? used;
    if (denominator <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, ((used - included) / denominator) * 100));
  }

  private formatMeterRate(plan: BillingPlan, meterKey: UsageMeterKey): string | null {
    const item = this.meterPriceItem(plan, meterKey);
    const unitAmount = item?.tiers?.find((tier) => (tier.unitAmount ?? 0) > 0)?.unitAmount;
    if (unitAmount == null) {
      return null;
    }
    const unitLabel = item?.unitLabel ?? this.defaultMeterBillingUnit(meterKey);
    return this.translate.instant('SETTINGS_BILLING.PLAN.METERS.RATE', {
      price: this.formatAmount(unitAmount, item?.currency),
      unit: unitLabel,
    });
  }

  private estimatedOverageCostMinor(
    plan: BillingPlan,
    meterKey: UsageMeterKey,
    overageQuantity: number
  ): number | null {
    if (overageQuantity <= 0) {
      return null;
    }
    const item = this.meterPriceItem(plan, meterKey);
    const unitAmount = item?.tiers?.find((tier) => (tier.unitAmount ?? 0) > 0)?.unitAmount;
    if (unitAmount == null) {
      return null;
    }
    return this.toBillingUnits(meterKey, overageQuantity) * unitAmount;
  }

  private toBillingUnits(meterKey: UsageMeterKey, quantity: number): number {
    if (meterKey === STORAGE_METER_KEY) {
      return Math.ceil(quantity / BYTES_PER_GB);
    }
    if (meterKey === AI_INPUT_TOKENS_METER_KEY || meterKey === AI_OUTPUT_TOKENS_METER_KEY) {
      return Math.ceil(quantity / AI_TOKENS_PER_BILLING_UNIT);
    }
    return quantity;
  }

  private defaultMeterBillingUnit(meterKey: UsageMeterKey): string {
    if (meterKey === STORAGE_METER_KEY) {
      return 'GB';
    }
    if (meterKey === AI_INPUT_TOKENS_METER_KEY || meterKey === AI_OUTPUT_TOKENS_METER_KEY) {
      return '1k tokens';
    }
    if (meterKey === SMS_METER_KEY) {
      return 'SMS';
    }
    return 'unit';
  }

  private formatUsageQuantity(meterKey: UsageMeterKey | 'seats', quantity: number): string {
    if (meterKey === STORAGE_METER_KEY) {
      return this.formatStorageBytes(quantity);
    }
    return new Intl.NumberFormat(this.platformI18n.resolveFormatLocale()).format(quantity);
  }

  private formatStorageBytes(bytes: number): string {
    const absBytes = Math.abs(bytes);
    const locale = this.platformI18n.resolveFormatLocale();
    if (absBytes < BYTES_PER_KB) {
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(bytes)} B`;
    }
    if (absBytes < BYTES_PER_MB) {
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / BYTES_PER_KB)} KB`;
    }
    if (absBytes < BYTES_PER_GB) {
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / BYTES_PER_MB)} MB`;
    }
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / BYTES_PER_GB)} GB`;
  }

  downloadInvoice(invoiceId: string): void {
    if (this.downloadingInvoiceId()) {
      return;
    }

    const invoiceWindow = globalThis.open('about:blank', '_blank');
    if (invoiceWindow) {
      invoiceWindow.opener = null;
    }

    this.downloadingInvoiceId.set(invoiceId);
    this.invoiceDownloadFailed.set(false);

    from(this.billingInvoicesApi.getBillingInvoiceDownloadUrl({ invoiceId }))
      .pipe(
        finalize(() => this.downloadingInvoiceId.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (!response.url) {
            invoiceWindow?.close();
            this.invoiceDownloadFailed.set(true);
            return;
          }

          if (invoiceWindow) {
            invoiceWindow.location.href = response.url;
          } else {
            const openedWindow = globalThis.open(response.url, '_blank', 'noopener');
            if (!openedWindow) {
              this.invoiceDownloadFailed.set(true);
            }
          }
        },
        error: () => {
          invoiceWindow?.close();
          this.invoiceDownloadFailed.set(true);
        },
      });
  }

  private loadOverview(): void {
    const requestId = ++this.overviewLoadRequestId;
    this.isLoadingOverview.set(true);
    this.overviewLoadFailed.set(false);
    this.overviewLoadErrorDetails.set(null);

    from(this.billingFrontendApi.getBillingOverview())
      .pipe(
        finalize(() => {
          if (this.overviewLoadRequestId === requestId) {
            this.isLoadingOverview.set(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (overview) => {
          if (this.overviewLoadRequestId !== requestId) {
            return;
          }
          this.applyOverview(overview);
          if (!overview.pendingChange) {
            this.planCancelFailed.set(false);
          }
          if (this.activeTab() === 'invoices') {
            this.loadInvoicesIfAvailable(overview);
          }
        },
        error: (error: unknown) => {
          if (this.overviewLoadRequestId !== requestId) {
            return;
          }
          if (!this.overviewHasLoadedOnce()) {
            this.overview.set(null);
          }
          this.overviewLoadFailed.set(true);
          void this.updateOverviewLoadErrorDetails(error, requestId);
        },
      });

    // Meter usage is loaded in parallel. Individual meter failures leave that meter at zero;
    // the rest of the billing tab still works.
    void this.loadUsageMeters();
    this.loadSpendLimits();
  }

  private async updateOverviewLoadErrorDetails(error: unknown, requestId: number): Promise<void> {
    const details = await resolveSettingsLoadErrorDetails(error);
    if (this.overviewLoadRequestId === requestId && this.overviewLoadFailed()) {
      this.overviewLoadErrorDetails.set(details);
    }
  }

  private async updateInvoiceLoadErrorDetails(error: unknown, requestId: number): Promise<void> {
    const details = await resolveSettingsLoadErrorDetails(error);
    if (this.invoiceLoadRequestId === requestId && this.invoicesLoadFailed()) {
      this.invoiceLoadErrorDetails.set(details);
    }
  }

  private loadSpendLimits(): void {
    this.isLoadingSpendLimits.set(true);
    this.spendLimitsLoadFailed.set(false);
    from(this.usageSpendLimitsApi.get())
      .pipe(
        finalize(() => this.isLoadingSpendLimits.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => this.applySpendLimits(response),
        error: () => {
          this.spendLimitsLoadFailed.set(true);
        },
      });
  }

  private applySpendLimits(response: UsageSpendLimitsResponseDto): void {
    this.spendLimits.set(response);
    const accountLimit =
      response.limits.find((limit) => limit.meterKey === ACCOUNT_SPEND_LIMIT_KEY) ?? null;
    this.applySpendCapDraft(accountLimit);
  }

  private applySpendCapDraft(
    accountLimit: UsageSpendLimitsResponseDto['limits'][number] | null
  ): void {
    if (!accountLimit) {
      this.spendCapPreset.set('amount-50');
      this.spendCapCustomMajor.set(50);
      this.spendCapDirty.set(false);
      return;
    }
    if (accountLimit.disabled) {
      this.spendCapPreset.set('unlimited');
      this.spendCapCustomMajor.set(
        accountLimit.amountMinor == null ? 50 : accountLimit.amountMinor / 100
      );
      this.spendCapDirty.set(false);
      return;
    }
    const major = accountLimit.amountMinor == null ? null : accountLimit.amountMinor / 100;
    const match =
      major != null
        ? SPEND_CAP_OPTIONS.find((opt) => opt.amount === major && opt.value !== 'custom')
        : undefined;
    if (match) {
      this.spendCapPreset.set(match.value);
      this.spendCapCustomMajor.set(major ?? 50);
    } else {
      this.spendCapPreset.set('custom');
      this.spendCapCustomMajor.set(major ?? 0);
    }
    this.spendCapDirty.set(false);
  }

  private async loadUsageMeters(): Promise<void> {
    this.isLoadingUsageMeters.set(true);
    try {
      const results = await Promise.allSettled(
        USAGE_METER_KEYS.map(async (meterKey) => {
          const value = await this.usageApi.getUsageMeterPeriodValue({ meterKey });
          return [meterKey, value] as const;
        })
      );

      const values: UsageMeterValueMap = { ...this.usageMeterValues() };
      const failedMeterKeys = new Set<UsageMeterKey>();
      for (const [index, result] of results.entries()) {
        if (result.status === 'fulfilled') {
          const [meterKey, value] = result.value;
          values[meterKey] = value;
        } else {
          const meterKey = USAGE_METER_KEYS[index];
          if (meterKey) {
            delete values[meterKey];
            failedMeterKeys.add(meterKey);
          }
        }
      }
      this.usageMeterValues.set(values);
      this.failedUsageMeterKeys.set(failedMeterKeys);
    } finally {
      this.isLoadingUsageMeters.set(false);
    }
  }

  private reconcilePendingPlanChange(checkoutSessionId: string): void {
    if (this.isReconcilingPlanChange()) {
      return;
    }

    this.isReconcilingPlanChange.set(true);
    from(this.billingFrontendApi.reconcilePendingBillingPlanChange())
      .pipe(
        finalize(() => this.isReconcilingPlanChange.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (overview) => {
          this.reconciledCheckoutSessionId = checkoutSessionId;
          this.applyOverview(overview);
        },
        error: () => {
          this.reconciledCheckoutSessionId = null;
          this.loadOverview();
        },
      });
  }

  private applyOverview(overview: BillingOverview): void {
    this.overview.set(overview);
    this.overviewHasLoadedOnce.set(true);
    this.overviewLoadFailed.set(false);
    this.overviewLoadErrorDetails.set(null);
    this.planChangeDialogTarget.set(null);
    if (overview.invoiceAccess.mode !== INVOICE_ACCESS_MODE.Download) {
      this.invoices.set([]);
      this.invoicesLoaded = false;
      this.invoicesHaveLoadedOnce.set(false);
      this.invoicesLoadFailed.set(false);
      this.invoiceLoadErrorDetails.set(null);
    }
    this.ensureActiveTabVisible();
  }

  private ensureActiveTabVisible(): void {
    if (this.isTabVisible(this.activeTab())) {
      return;
    }
    this.activeTab.set('plan');
    syncTabToUrl(this.router, this.route, 'tab', 'plan');
  }

  private isTabVisible(tab: BillingTab): boolean {
    return tab !== 'cancellation' || this.canShowCancellationTab();
  }

  private loadInvoicesIfAvailable(overview = this.overview()): void {
    if (
      overview?.invoiceAccess.mode !== INVOICE_ACCESS_MODE.Download ||
      this.invoicesLoaded ||
      this.isLoadingInvoices()
    ) {
      return;
    }

    const requestId = ++this.invoiceLoadRequestId;
    this.isLoadingInvoices.set(true);
    this.invoicesLoadFailed.set(false);
    this.invoiceLoadErrorDetails.set(null);

    from(this.billingInvoicesApi.listBillingInvoices())
      .pipe(
        finalize(() => {
          if (this.invoiceLoadRequestId === requestId) {
            this.isLoadingInvoices.set(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (this.invoiceLoadRequestId !== requestId) {
            return;
          }
          this.invoices.set(this.toBillingInvoiceRows(response));
          this.invoicesLoaded = true;
          this.invoicesHaveLoadedOnce.set(true);
          this.invoicesLoadFailed.set(false);
          this.invoiceLoadErrorDetails.set(null);
        },
        error: (error: unknown) => {
          if (this.invoiceLoadRequestId !== requestId) {
            return;
          }
          if (!this.invoicesHaveLoadedOnce()) {
            this.invoices.set([]);
          }
          this.invoicesLoadFailed.set(true);
          void this.updateInvoiceLoadErrorDetails(error, requestId);
        },
      });
  }

  private planSwitchFailureKey(error: unknown): string {
    switch (this.billingFailureReason(error)) {
      case BILLING_PROBLEM.PlanChangeInProgress:
        return 'SETTINGS_BILLING.PLAN_CARDS.ERROR_PENDING_CHANGE';
      case BILLING_PROBLEM.PlanChangeNotAllowed:
        return 'SETTINGS_BILLING.PLAN_CARDS.ERROR_NOT_ALLOWED';
      case BILLING_PROBLEM.TenantBillingLocked:
        return 'SETTINGS_BILLING.PLAN_CARDS.ERROR_BILLING_LOCKED';
      case BILLING_PROBLEM.StripeBillingUnavailable:
        return 'SETTINGS_BILLING.PLAN_CARDS.ERROR_STRIPE_UNAVAILABLE';
      default:
        return 'SETTINGS_BILLING.PLAN_CARDS.SWITCH_FAILED';
    }
  }

  private planCancelFailureKey(error: unknown): string {
    switch (this.billingFailureReason(error)) {
      case BILLING_PROBLEM.PlanChangeInProgress:
        return 'SETTINGS_BILLING.PENDING_CHANGE.CANCEL_FAILED_IN_PROGRESS';
      case BILLING_PROBLEM.PlanChangeNotAllowed:
        return 'SETTINGS_BILLING.PENDING_CHANGE.CANCEL_FAILED_NOT_ALLOWED';
      case BILLING_PROBLEM.TenantBillingLocked:
        return 'SETTINGS_BILLING.PENDING_CHANGE.CANCEL_FAILED_BILLING_LOCKED';
      case BILLING_PROBLEM.StripeBillingUnavailable:
        return 'SETTINGS_BILLING.PENDING_CHANGE.CANCEL_FAILED_STRIPE';
      default:
        return 'SETTINGS_BILLING.PENDING_CHANGE.CANCEL_FAILED';
    }
  }

  private seatChangeFailureKey(error: unknown): string {
    switch (this.billingFailureReason(error)) {
      case BILLING_PROBLEM.PlanChangeInProgress:
        return 'SETTINGS_BILLING.PLAN.SEATS.ERROR_PENDING_CHANGE';
      case BILLING_PROBLEM.PlanChangeNotAllowed:
        return 'SETTINGS_BILLING.PLAN.SEATS.ERROR_NOT_ALLOWED';
      case BILLING_PROBLEM.TenantBillingLocked:
        return 'SETTINGS_BILLING.PLAN.SEATS.ERROR_BILLING_LOCKED';
      case BILLING_PROBLEM.StripeBillingUnavailable:
        return 'SETTINGS_BILLING.PLAN.SEATS.ERROR_STRIPE_UNAVAILABLE';
      default:
        return 'SETTINGS_BILLING.PLAN.SEATS.CHANGE_FAILED';
    }
  }

  private billingFailureReason(error: unknown): string | null {
    const type = this.problemType(error);
    if (type) {
      return type;
    }

    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    if (error.status === 402) {
      return BILLING_PROBLEM.TenantBillingLocked;
    }
    if (error.status === 409) {
      return BILLING_PROBLEM.PlanChangeInProgress;
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return BILLING_PROBLEM.StripeBillingUnavailable;
    }

    return null;
  }

  private problemType(error: unknown): string | null {
    if (error instanceof HttpErrorResponse && typeof error.error?.type === 'string') {
      return error.error.type;
    }
    const maybeType = (error as { error?: { type?: unknown } })?.error?.type;
    return typeof maybeType === 'string' ? maybeType : null;
  }

  private toBillingInvoiceRows(response: PublicBillingInvoiceList): BillingInvoiceTableRow[] {
    return (response.items ?? [])
      .filter(
        (invoice): invoice is PublicBillingInvoiceSummary & { id: string } =>
          typeof invoice.id === 'string'
      )
      .map((invoice) => ({
        id: invoice.id,
        number: invoice.number,
        status: invoice.status,
        currency: invoice.currency,
        amountDue: invoice.amountDue,
        amountPaid: invoice.amountPaid,
        amountRemaining: invoice.amountRemaining,
        createdAt: this.toDate(invoice.createdAt),
        periodStart: this.toDate(invoice.periodStart),
        periodEnd: this.toDate(invoice.periodEnd),
        amountDisplay: this.formatAmount(invoice.amountPaid ?? invoice.amountDue, invoice.currency),
      }));
  }

  private toDate(value: Date | string | null | undefined): Date | undefined {
    if (value == null) {
      return undefined;
    }
    return value instanceof Date ? value : new Date(value);
  }

  private meteredObjectPriceItem(plan: BillingPlan): BillingPlanPriceItem | null {
    return (
      this.planPriceItems(plan).find((item) => {
        return item.meterKey === CUSTOM_OBJECTS_METER_KEY;
      }) ?? null
    );
  }

  private objectsIncludedCell(plan: BillingPlan): PlanCompareCell {
    if (plan.planKind === PLAN_KIND.Enterprise) {
      return {
        kind: 'text',
        value: 'SETTINGS_BILLING.COMPARE.UNLIMITED',
        isTranslationKey: true,
      };
    }

    const item = this.meteredObjectPriceItem(plan);
    const freeTier = item?.tiers?.find((tier) => tier.unitAmount === 0 && tier.upTo != null);
    if (freeTier?.upTo != null) {
      return {
        kind: 'text',
        value: new Intl.NumberFormat(this.platformI18n.resolveFormatLocale()).format(freeTier.upTo),
      };
    }

    return STATIC_CELL_DASH;
  }

  private extraObjectPricingCell(plan: BillingPlan): PlanCompareCell {
    if (plan.planKind === PLAN_KIND.Enterprise) {
      return {
        kind: 'text',
        value: 'SETTINGS_BILLING.COMPARE.NEGOTIATED',
        isTranslationKey: true,
      };
    }

    const item = this.meteredObjectPriceItem(plan);
    if (!item) {
      return STATIC_CELL_DASH;
    }

    const paidTier =
      item.tiers?.find((tier) => (tier.unitAmount ?? 0) > 0) ??
      item.tiers?.find((tier) => (tier.flatAmount ?? 0) > 0);
    if (paidTier) {
      return {
        kind: 'text',
        value: this.formatAmount(paidTier.unitAmount ?? paidTier.flatAmount, item.currency),
      };
    }

    return STATIC_CELL_DASH;
  }

  /**
   * The metered Stripe price item driving storage overage on this plan. Plans without
   * document-management or without a metered overage price (FREE / ENTERPRISE) return `null`.
   */
  private meteredStoragePriceItem(plan: BillingPlan): BillingPlanPriceItem | null {
    return this.planPriceItems(plan).find((item) => item.meterKey === STORAGE_METER_KEY) ?? null;
  }

  /**
   * Renders the plan-level included storage threshold formatted as MB or GB. Plans without
   * document-management entitlement show `—` to keep no-feature plans visually quiet.
   */
  private storageIncludedCell(plan: BillingPlan): PlanCompareCell {
    if (!this.hasDocumentManagementEntitlement(plan)) {
      return STATIC_CELL_DASH;
    }
    if (plan.planKind === PLAN_KIND.Enterprise) {
      return {
        kind: 'text',
        value: 'SETTINGS_BILLING.COMPARE.NEGOTIATED',
        isTranslationKey: true,
      };
    }
    const includedBytes = plan.meterEntitlements?.find(
      (entitlement) => entitlement.meterKey === STORAGE_METER_KEY
    )?.includedQuantity;
    if (includedBytes == null) {
      return STATIC_CELL_DASH;
    }
    return { kind: 'text', value: this.formatStorageMb(includedBytes / BYTES_PER_MB) };
  }

  /**
   * Surfaces the per-MB Stripe overage rate from tier 2 of the metered storage price for
   * self-service plans, "Negotiated" for Enterprise, `—` for Free or for plans without metered
   * storage. Currency comes from the price item itself, with the plan's billing currency as the
   * fallback.
   */
  private extraStoragePricingCell(plan: BillingPlan): PlanCompareCell {
    if (!this.hasDocumentManagementEntitlement(plan)) {
      return STATIC_CELL_DASH;
    }
    if (plan.planKind === PLAN_KIND.Enterprise) {
      return {
        kind: 'text',
        value: 'SETTINGS_BILLING.COMPARE.NEGOTIATED',
        isTranslationKey: true,
      };
    }
    const item = this.meteredStoragePriceItem(plan);
    if (!item) {
      return STATIC_CELL_DASH;
    }
    const paidTier =
      item.tiers?.find((tier) => (tier.unitAmount ?? 0) > 0) ??
      item.tiers?.find((tier) => (tier.flatAmount ?? 0) > 0);
    if (!paidTier) {
      return STATIC_CELL_DASH;
    }
    return {
      kind: 'text',
      value: this.formatAmount(paidTier.unitAmount ?? paidTier.flatAmount, item.currency),
    };
  }

  private meterIncludedCell(plan: BillingPlan, meterKey: UsageMeterKey): PlanCompareCell {
    if (plan.planKind === PLAN_KIND.Enterprise) {
      return {
        kind: 'text',
        value: 'SETTINGS_BILLING.COMPARE.NEGOTIATED',
        isTranslationKey: true,
      };
    }

    const entitlement = this.meterEntitlement(plan, meterKey);
    const included = this.meterIncludedQuantity(plan, meterKey, entitlement);
    if (included == null || included <= 0) {
      return STATIC_CELL_DASH;
    }
    return {
      kind: 'text',
      value: this.formatUsageQuantity(meterKey, included),
    };
  }

  private extraMeterPricingCell(plan: BillingPlan, meterKey: UsageMeterKey): PlanCompareCell {
    if (plan.planKind === PLAN_KIND.Enterprise) {
      return {
        kind: 'text',
        value: 'SETTINGS_BILLING.COMPARE.NEGOTIATED',
        isTranslationKey: true,
      };
    }

    const item = this.meterPriceItem(plan, meterKey);
    if (!item) {
      return STATIC_CELL_DASH;
    }

    const paidTier =
      item.tiers?.find((tier) => (tier.unitAmount ?? 0) > 0) ??
      item.tiers?.find((tier) => (tier.flatAmount ?? 0) > 0);
    if (!paidTier) {
      return STATIC_CELL_DASH;
    }

    const unitLabel = item.unitLabel ?? this.defaultMeterBillingUnit(meterKey);
    return {
      kind: 'text',
      value: `${this.formatAmount(paidTier.unitAmount ?? paidTier.flatAmount, item.currency)} / ${unitLabel}`,
    };
  }

  private hasDocumentManagementEntitlement(plan: BillingPlan): boolean {
    if (plan.planKind === PLAN_KIND.Enterprise) return true;
    return plan.entitlementFeatureKeys.includes('document-management');
  }

  private formatStorageMb(mb: number): string {
    if (mb < 1000) return `${mb} MB`;
    const gb = mb / 1000;
    const rounded = Math.round(gb * 10) / 10;
    return `${rounded} GB`;
  }

  private entitlementFeatureRows(plans: BillingPlan[]): PlanCompareRow[] {
    const allKeys = new Set<string>();
    for (const plan of plans) {
      for (const key of plan.entitlementFeatureKeys) {
        allKeys.add(key);
      }
    }

    return Array.from(allKeys)
      .sort((first, second) => this.compareEntitlementFeatureKeys(first, second))
      .map<PlanCompareRow>((featureKey) => ({
        id: `feature:${featureKey}`,
        labelKey: this.featureKeyLabel(featureKey),
        cells: plans.map<PlanCompareCell>((plan) => {
          if (plan.planKind === PLAN_KIND.Enterprise) {
            return STATIC_CELL_CHECK;
          }
          return plan.entitlementFeatureKeys.includes(featureKey)
            ? STATIC_CELL_CHECK
            : STATIC_CELL_DASH;
        }),
      }));
  }

  private featureKeyLabel(featureKey: string): string {
    const translationKey = `SETTINGS_BILLING.FEATURES.${featureKey
      .replaceAll(/[^a-zA-Z0-9]+/g, '_')
      .toUpperCase()}`;
    const translated = this.translate.instant(translationKey);
    return translated === translationKey ? this.formatFeatureKey(featureKey) : translated;
  }

  private compareEntitlementFeatureKeys(first: string, second: string): number {
    const firstIndex = ENTITLEMENT_FEATURE_ORDER.get(first);
    const secondIndex = ENTITLEMENT_FEATURE_ORDER.get(second);

    if (firstIndex != null && secondIndex != null) {
      return firstIndex - secondIndex;
    }
    if (firstIndex != null) {
      return -1;
    }
    if (secondIndex != null) {
      return 1;
    }
    return first.localeCompare(second);
  }

  private primarySeatPriceItem(plan: BillingPlan): BillingPlanPriceItem | null {
    return this.planPriceItems(plan).find((item) => item.unitLabel === 'seat') ?? null;
  }

  private primaryBillablePriceItem(plan: BillingPlan): BillingPlanPriceItem | null {
    return (
      this.primarySeatPriceItem(plan) ??
      this.planPriceItems(plan).find((item) => item.unitAmount != null) ??
      null
    );
  }

  private planSeatSortAmount(plan: BillingPlan): number {
    if (plan.planKind === PLAN_KIND.Free) return 0;
    return this.primarySeatPriceItem(plan)?.unitAmount ?? Number.MAX_SAFE_INTEGER;
  }

  private primaryInterval(plan: BillingPlan): string | null {
    return (
      this.primarySeatPriceItem(plan)?.interval ??
      this.planPriceItems(plan).find((item) => item.interval)?.interval ??
      null
    );
  }

  private meteredObjectPriceSummary(
    item: BillingPlanPriceItem,
    fallbackCurrency?: string | null
  ): string | null {
    const isObjectPricing = item.meterKey === CUSTOM_OBJECTS_METER_KEY;
    const tiers = item.tiers ?? [];
    if (!isObjectPricing || tiers.length === 0) {
      return null;
    }

    const freeTier = tiers.find((tier) => tier.unitAmount === 0 && tier.upTo != null);
    const paidTier =
      tiers.find((tier) => (tier.unitAmount ?? 0) > 0) ??
      tiers.find((tier) => (tier.flatAmount ?? 0) > 0);
    const price = paidTier
      ? this.formatAmount(
          paidTier.unitAmount ?? paidTier.flatAmount,
          item.currency ?? fallbackCurrency
        )
      : null;
    const interval = item.interval ? this.translatedIntervalSuffix(item.interval) : '';

    if (freeTier?.upTo != null && price) {
      return this.translate.instant('SETTINGS_BILLING.PLAN_CARDS.OBJECT_PRICE_WITH_FREE_TIER', {
        count: freeTier.upTo,
        price,
        interval,
      });
    }

    if (price) {
      return this.translate.instant('SETTINGS_BILLING.PLAN_CARDS.OBJECT_PRICE', {
        price,
        interval,
      });
    }

    if (freeTier?.upTo != null) {
      return this.translate.instant('SETTINGS_BILLING.PLAN_CARDS.OBJECTS_INCLUDED', {
        count: freeTier.upTo,
      });
    }

    return null;
  }

  private translatedIntervalSuffix(interval: string): string {
    const normalizedInterval = interval.toUpperCase();
    if (['DAY', 'WEEK', 'MONTH', 'YEAR'].includes(normalizedInterval)) {
      return this.translate.instant(`SETTINGS_BILLING.PLAN_CARDS.INTERVAL.${normalizedInterval}`);
    }
    return `/ ${interval}`;
  }

  private readBillingTab(queryParams: Record<string, string>): BillingTab {
    return readTabFromQueryParams(queryParams, 'tab', TAB_VALUES, 'plan') as BillingTab;
  }
}
